/**
 * The Hiring dashboard's Download (the product owner, 28 Oct 2026): the server's answer
 * (POST /hiring-links/:projectId/export) as the three sheets of one Excel file, written in the
 * browser by utils/tableXlsx - the Store Visits "Full list (Excel)" pattern.
 *
 *   Openings    one row per opening of the tab and search (all pages), in the dashboard's
 *               order: the row as the dashboard words it, then the panel's details and team,
 *               and a Read column that says whether its figures were read just now and, if
 *               not, why.
 *   Candidates  one row per application Recruitment lists, grouped by opening in the Openings
 *               order, newest first within each. The opening's Store, Store code, Opening and
 *               Opening ID are on every row, so a filter in Excel needs no lookup; an
 *               opening's Candidates figure is its number of rows here, as both come from the
 *               same read. A shared opening is one row per store on the dashboard, so its
 *               candidates are listed under each of those stores.
 *   About       when it was downloaded, the tab and the search, the two counts, and four fixed
 *               notes.
 *
 * What could not be read is SAID, never left as a 0: "Could not be read" in the Candidates
 * cell, and the Read column for figures from the last read or not read at all. Mobile numbers
 * are written as text, so a leading + or 0 survives and Excel never turns one into a number.
 * The file name holds the tab and the day only - never the search or anybody's name.
 *
 * Pure: the page fetches and saves; this only turns an answer into sheets, so a test can hold
 * every cell without drawing a page.
 */
import { fileSlug, type TableCell, type TableSheet } from '../../utils/tableXlsx';
import {
  OPENING_STAGE_WORDS,
  type HiringExport,
  type HiringExportCandidateRow,
  type HiringExportOpening,
  type OpeningTeamPerson,
  type OpeningTeamRound,
} from '../../services/hiringOpeningApi';
import type { HiringDashStatus } from '../../services/api';
import {
  DASHBOARD_WORDS,
  DASH_STATUS_WORDS,
  DOWNLOAD_WORDS,
  attentionHover,
  dashPlaceOf,
  dashWordOfLink,
  designationOf,
} from './hiringText.dashboard';
import {
  OPENING_WORDS,
  candidateSourceWord,
  deletedInRecruitment,
  formatHiringDay,
  formatHiringMoment,
  hiringErrorText,
  hiringStatusOf,
  openingNames,
  publishedWords,
} from './hiringText';

const INDIA = 'Asia/Kolkata';

/** The tab's word: "All", or one of the four statuses. */
export const exportTabWord = (tab: HiringDashStatus | null): string => (tab && DASH_STATUS_WORDS[tab]) || DASHBOARD_WORDS.all;

/**
 * The file's name: hiring-<tab>-<yyyy-mm-dd>.xlsx, the day in India time ("hiring-all-2026-10-28.xlsx",
 * "hiring-needs_attention-2026-10-28.xlsx"). It is never given the search: a store's or a
 * person's name typed into the box must not end up in a Downloads folder's list of names.
 */
export const hiringExportFileName = (tab: HiringDashStatus | null, day: Date): string =>
  `hiring-${fileSlug(exportTabWord(tab))}-${day.toLocaleDateString('en-CA', { timeZone: INDIA })}.xlsx`;

/** The Store cell: the store's name (its code when it has none), or "No store" for a hiring with no store. */
const storeCell = (opening: HiringExportOpening): string => {
  if (opening.subject.kind !== 'store') return DASHBOARD_WORDS.noStore;
  return (opening.subject.storeName ?? '').trim() || (opening.subject.storeCode ?? '').trim() || 'A store';
};

const storeCodeCell = (opening: HiringExportOpening): string =>
  (opening.subject.kind === 'store' ? (opening.subject.storeCode ?? '').trim() : '');

/** The four cells that name an opening, on both sheets alike: how the two sheets are linked. */
const openingKey = (opening: HiringExportOpening): TableCell[] => [
  storeCell(opening),
  storeCodeCell(opening),
  designationOf(opening),
  opening.openingId,
];

/** Why the opening is wanted: "Replacement of 910007", "New store", "Extra headcount"; blank for a hand link saved with no reason. */
export const exportReasonWords = (opening: Pick<HiringExportOpening, 'reason' | 'replacesEmpCode'>): string => {
  const code = (opening.replacesEmpCode ?? '').trim();
  if (opening.reason === 'replacement') return code ? DOWNLOAD_WORDS.replacementOf(code) : DOWNLOAD_WORDS.reasons.replacement;
  if (opening.reason === 'new_store') return DOWNLOAD_WORDS.reasons.newStore;
  if (opening.reason === 'extra_headcount') return DOWNLOAD_WORDS.reasons.extraHeadcount;
  return '';
};

/** "Raised 02 Mar 2031 · Sample Name (910002)": raised or linked here, the day, and by whom - the name and the code. */
export const exportCreatedWords = (created: HiringExportOpening['created']): string => {
  const kind = created.kind === 'raised' ? DOWNLOAD_WORDS.created.raised : DOWNLOAD_WORDS.created.linked;
  const name = String(created.byName ?? '').trim();
  const code = String(created.byCode ?? '').trim();
  const who = name && code ? `${name} (${code})` : name || code;
  const day = created.on ? formatHiringDay(created.on) : '';
  return [[kind, day].filter(Boolean).join(' '), who].filter(Boolean).join(' · ');
};

/** The Read column, from the server's mark (hiring-opening.logic.ts exportReadOf). */
export const exportReadWords = (opening: Pick<HiringExportOpening, 'read' | 'seenAt' | 'status'>): string => {
  switch (opening.read) {
    case 'now': return DOWNLOAD_WORDS.read.now;
    case 'last_read': return opening.seenAt ? DOWNLOAD_WORDS.read.lastRead(formatHiringMoment(opening.seenAt)) : DOWNLOAD_WORDS.read.neverRead;
    case 'being_raised': return DOWNLOAD_WORDS.read.beingRaised;
    case 'no_identity': return DOWNLOAD_WORDS.read.noIdentity;
    // Closed by Recruitment itself is "Cancelled in Recruitment", the word the Status column and
    // the screen use; only an opening a good read no longer finds at all is "Deleted".
    case 'deleted': return opening.status === 'closed_in_recruitment' ? DOWNLOAD_WORDS.read.cancelled : DOWNLOAD_WORDS.read.deleted;
    default: return '';
  }
};

/** The Candidates cell: how many rows the Candidates sheet has for it; "Could not be read" - never 0 - when they were not read; blank when there is nothing to read. */
export const exportCandidatesCell = (opening: Pick<HiringExportOpening, 'candidates'>): TableCell => {
  if (opening.candidates.read === 'ok') return opening.candidates.rows.length;
  if (opening.candidates.read === 'failed') return DOWNLOAD_WORDS.candidatesUnread;
  return null;
};

/** Recruiters or offer makers, as the panel lists them: names, "–" for nobody. */
const peopleCell = (people: readonly OpeningTeamPerson[]): string => openingNames(people).join(', ') || OPENING_WORDS.none;

/** One round as the panel says it: the interviewer, "Client", or the client's name and "Client" for a holder. */
const roundWho = (round: OpeningTeamRound): string => {
  if (round.kind === 'client') return round.name ? `${round.name} · ${OPENING_WORDS.client}` : OPENING_WORDS.client;
  if (round.kind === 'interviewer') return String(round.name ?? '').trim() || OPENING_WORDS.none;
  return OPENING_WORDS.none;
};

/** "Round 1: Sample Interviewer; Round 2: Client", in round order. */
export const exportRoundsWords = (rounds: readonly OpeningTeamRound[]): string =>
  [...rounds].sort((a, b) => a.round - b.round).map((round) => `${OPENING_WORDS.team.round(round.round)}: ${roundWho(round)}`).join('; ');

/** A count cell, blank where nothing was read. */
const count = (n: number | null | undefined): TableCell => (typeof n === 'number' && Number.isFinite(n) ? n : null);

/** One row of the Openings sheet, in DOWNLOAD_WORDS.openingColumns' order. */
const openingRow = (opening: HiringExportOpening): TableCell[] => {
  const place = dashPlaceOf(opening);
  // Deleted in Recruitment: the last numbers read are not what stands now, so none is written - as the dashboard shows none.
  const numbers = deletedInRecruitment(opening) ? null : opening.numbers;
  return [
    ...openingKey(opening),
    place.address,
    place.city,
    place.state,
    opening.pincode,
    dashWordOfLink(opening),
    attentionHover(opening),
    exportReasonWords(opening),
    count(numbers?.positions),
    count(numbers?.filled),
    count(numbers?.offered),
    count(numbers?.open),
    exportCandidatesCell(opening),
    publishedWords(opening.published),
    exportCreatedWords(opening.created),
    opening.team ? peopleCell(opening.team.recruiters) : null,
    opening.team ? peopleCell(opening.team.offerMakers) : null,
    opening.team ? exportRoundsWords(opening.team.rounds) : null,
    formatHiringMoment(opening.updatedAt),
    exportReadWords(opening),
  ];
};

/** A candidate's Stage cell: the panel's word, "Joined (left)" for someone who has since left; blank for a status with no word. */
export const exportStageWords = (row: Pick<HiringExportCandidateRow, 'stage' | 'leftAfterJoining'>): string => {
  // Exited or moved on after joining here: the server's word for them is Joined.
  if (row.leftAfterJoining) return DOWNLOAD_WORDS.joinedLeft;
  return row.stage ? OPENING_STAGE_WORDS[row.stage] : '';
};

/** A candidate's Round cell: "Round 1", "Round 2 done", or blank when the status names none. */
export const exportRoundWords = (row: Pick<HiringExportCandidateRow, 'round' | 'roundDone'>): string => {
  if (row.round == null) return '';
  return row.roundDone ? OPENING_WORDS.roundCell.done(row.round) : OPENING_WORDS.roundCell.at(row.round);
};

/** One row of the Candidates sheet, in DOWNLOAD_WORDS.candidateColumns' order. The mobile is a string: written as text. */
const candidateRow = (key: TableCell[], row: HiringExportCandidateRow): TableCell[] => [
  ...key,
  row.name,
  row.mobile ? String(row.mobile) : null,
  row.source ? candidateSourceWord(row.source) : null,
  exportStageWords(row),
  exportRoundWords(row),
  row.appliedOn ? formatHiringDay(row.appliedOn) : null,
];

/**
 * The three sheets of the file, from the server's answer: Openings, Candidates and About, in
 * that order. `now` is the moment written as "Downloaded at".
 */
export const hiringExportSheets = (answer: HiringExport, now: Date = new Date()): TableSheet[] => {
  const openings = answer.openings ?? [];
  const candidates = openings.flatMap((opening) => {
    if (opening.candidates.read !== 'ok') return [];
    const key = openingKey(opening);
    return opening.candidates.rows.map((row) => candidateRow(key, row));
  });
  const about = DOWNLOAD_WORDS.about;
  return [
    { name: DOWNLOAD_WORDS.sheets.openings, headers: [...DOWNLOAD_WORDS.openingColumns], rows: openings.map(openingRow) },
    { name: DOWNLOAD_WORDS.sheets.candidates, headers: [...DOWNLOAD_WORDS.candidateColumns], rows: candidates },
    {
      name: DOWNLOAD_WORDS.sheets.about,
      headers: [...DOWNLOAD_WORDS.aboutColumns],
      rows: [
        [about.downloadedAt, formatHiringMoment(now.toISOString())],
        [about.tab, exportTabWord(answer.status)],
        [about.search, answer.q || about.noSearch],
        [about.openings, openings.length],
        // The rows of the Candidates sheet: what the file holds.
        [about.candidates, candidates.length],
        ...(answer.capped ? [[about.capped, about.cappedNote]] : []),
        ...DOWNLOAD_WORDS.notes.map((note, i) => [about.note(i + 1), note]),
      ],
    },
  ];
};

/** What the page says once the file is saved: the openings not read, and the ones left out; null when there is nothing to say. */
export const hiringExportSavedLine = (answer: Pick<HiringExport, 'counts' | 'capped'>): string | null => {
  const unread = answer.counts?.unread ?? 0;
  const parts = [
    unread > 0 ? DOWNLOAD_WORDS.savedUnread(unread) : null,
    answer.capped ? DOWNLOAD_WORDS.savedCapped : null,
  ].filter((part): part is string => !!part);
  return parts.length > 0 ? [DOWNLOAD_WORDS.saved, ...parts].join(' ') : null;
};

/**
 * What a refused or failed download says under the toolbar: the server's own sentence for a 429
 * (another download is being prepared) and for a 403 (the hiring access answer); for anything
 * else - no answer, a broken answer, the server failing, the file not written - one plain line.
 */
export const hiringExportErrorText = (err: unknown): string => {
  const status = hiringStatusOf(err);
  if (status === 429) return hiringErrorText(err, DOWNLOAD_WORDS.busy);
  if (status === 403) return hiringErrorText(err, DOWNLOAD_WORDS.failed);
  return DOWNLOAD_WORDS.failed;
};
